import { useContext } from 'react'
import uuid from 'react-uuid'
import { db, timestamp } from '../firebase/config'
import { client } from './Client'
import { SavedIcon } from '../StateManagment/SavedIcon'
import { Auth } from '../StateManagment/Auth'
import softDeleteDoc from '../helpers/softDeleteDoc'

/**
 * De bewerkacties op de losse antwoorden van één vraag.
 *
 * Twee schermen tonen die lijst — alle antwoorden op een vraag, en de
 * antwoorden van één deelnemer — en deden tot nu toe hetzelfde werk in eigen
 * kopieën. Ze verschillen alleen hierin dat een nieuw antwoord op het
 * deelnemerscherm aan die deelnemer gekoppeld wordt.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {object} params
 * @param {string} params.fieldID - De vraag waar de antwoorden bij horen.
 * @param {string} params.momentID - Het gekozen meetmoment.
 * @param {string} params.researchId - Het onderzoek.
 * @param {string} params.questionaireId - De vragenlijst (veldnaam bevat een oude typefout in de data).
 * @param {string} [params.fieldType] - `Type` van de vraag. Gaat mee op een nieuw
 *   antwoord: zonder dat veld weet de weergave niet wat voor antwoord het is
 *   (inbox #190).
 * @param {string} [params.personaId] - De deelnemer, op het deelnemerscherm.
 * @param {Array<object>} params.results - De antwoorden die nu in beeld staan; bepaalt de positie van een nieuw antwoord.
 * @returns {{inputHandler: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void, deleteResult: (event: React.MouseEvent<HTMLElement>) => void, addResult: () => void}}
 */
const useResearchResponseActions = ({ fieldID, momentID, researchId, questionaireId, fieldType, personaId, results }) => {
    const [, setSaved, , setSavedMessage] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    const rows = Array.isArray(results) ? results : []

    // Sla het gewijzigde antwoord op
    const inputHandler = (e) => {
        const input = e.target.value
        const docid = e.target.dataset.docid

        db.collection('QuestionnairesResponses')
            .doc(docid)
            .update({
                Input: input,
            })
            .then(() => {
                setSaved('flex')
            })
    }

    // Markeer het antwoord als verwijderd (ADR-008): weg voor de gebruiker,
    // maar terug te halen uit de prullenbak. De titel komt van de knop mee,
    // want zonder titel is de prullenbak een lijst met document-ids.
    const deleteResult = (e) => {
        const { docid, rowtitle, fieldtitle } = e.currentTarget.dataset

        softDeleteDoc({
            collection: 'QuestionnairesResponses',
            docid,
            authId: auth?.ID,
            title: rowtitle || fieldtitle || 'Antwoord',
        }).then(() => {
            // Niet de standaardmelding 'Opgeslagen': dat is precies het
            // verkeerde woord na het weggooien van iets, en het verzweeg dat
            // het terug te halen is (inbox #193).
            setSavedMessage('Antwoord naar de prullenbak')
        }).catch((error) => {
            // Zichtbaar maken in plaats van slikken: de kaart blijft staan, en
            // zonder melding lijkt de klik niets te doen.
            // AI-CONTEXT: See docs/pitfalls.md#error-catch-all
            console.error('Antwoord verwijderen mislukt', { docid }, error)
            setSavedMessage('Verwijderen is mislukt. Het antwoord staat er nog.')
        })
    }

    // Voeg een leeg antwoord toe
    const addResult = () => {
        const docid = uuid()

        db.collection('QuestionnairesResponses')
            .doc(docid)
            .set({
                FieldID: fieldID,
                // Het vraagtype hoort op het antwoord: de weergave leidt eruit
                // af of het een cijfer of een tekst is, en de volgorde van een
                // matrixvraag hangt ervan af (inbox #190).
                FieldType: fieldType || '',
                Input: '',
                Timestamp: timestamp,
                CompagnyID: client,
                QuestionannaireID: questionaireId,
                MomentID: momentID,
                ResearchID: researchId,
                // Leeg op het scherm dat over het hele onderzoek gaat. Het veld
                // wél schrijven, want een ontbrekend veld valt buiten elke
                // sortering op Persona.
                // AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
                Persona: personaId || '',
                ID: uuid(),
                OptionType: '',
                Position: rows.length + 1,
            })
            .then(() => {
                setSaved('flex')
            })
    }

    return { inputHandler, deleteResult, addResult }
}

export default useResearchResponseActions
